/**
 * CacheOrchestrator - Unified cache coordinator
 * 
 * Coordinates between L1 (memory/LRU) and L2 (IndexedDB) cache layers.
 * Provides a single interface for all cache operations with automatic
 * promotion and demotion between layers.
 * 
 * Architecture:
 * ┌─────────────────────────────────────────────────────────┐
 * │  CacheOrchestrator                                       │
 * │  ┌─────────────────────────────────────────────────────┐│
 * │  │  L1: Memory Cache (LRU, fast, limited size)         ││
 * │  │  - Hot data: visible pages + nearby                 ││
 * │  │  - Max: 100 pages                                   ││
 * │  └─────────────────────────────────────────────────────┘│
 * │                          │                              │
 * │                    promote/demote                        │
 * │                          │                              │
 * │  ┌─────────────────────────────────────────────────────┐│
 * │  │  L2: IndexedDB (persistent, slow, large)            ││
 * │  │  - Survives refresh/restart                         ││
 * │  │  - Max: 2000 pages                                  ││
 * │  └─────────────────────────────────────────────────────┘│
 * └─────────────────────────────────────────────────────────┘
 */

import type { PageLayout } from '../../../core/types/Typesetter';
import { generatePageCacheKey } from './CacheKeyGenerator';
import { LRUCacheEntry, createLRUEntry, touchLRUEntry, evictLRUEntries } from './LRUCacheEntry';
import { IndexedDBCache, getIndexedDBCache } from './IndexedDBCache';
import { CacheLogger } from '../../../../../utils/loggers/CacheLogger';

// L1 cache configuration
const L1_MAX_SIZE = 100;
const L1_EVICTION_BATCH = 20; // Evict this many when cleaning up

/**
 * Cache lookup result with source information
 */
export interface CacheLookupResult {
  page: PageLayout | null;
  source: 'l1' | 'l2' | 'miss';
  latencyMs: number;
}

/**
 * Batch lookup result
 */
export interface BatchLookupResult {
  pages: Map<number, PageLayout>;
  l1Hits: number;
  l2Hits: number;
  misses: number[];
  latencyMs: number;
}

/**
 * Cache statistics
 */
export interface CacheOrchestratorStats {
  l1: {
    size: number;
    maxSize: number;
    hitRate: number;
  };
  l2: {
    totalEntries: number;
    uniqueDocuments: number;
  };
  session: {
    l1Hits: number;
    l2Hits: number;
    misses: number;
  };
}

export class CacheOrchestrator {
  // L1: Memory cache with LRU tracking
  private l1Cache = new Map<string, LRUCacheEntry<PageLayout>>();
  
  // L2: IndexedDB persistent cache
  private l2Cache: IndexedDBCache;
  
  // Current document context
  private currentDocumentId: string = '';
  private currentContentHash: string = '';
  
  // Session statistics
  private sessionStats = {
    l1Hits: 0,
    l2Hits: 0,
    misses: 0
  };
  
  // Cleanup interval
  private cleanupInterval: ReturnType<typeof setInterval> | null = null;
  
  constructor(l2Cache?: IndexedDBCache) {
    this.l2Cache = l2Cache || getIndexedDBCache();
    
    // Initialize L2 cache
    this.l2Cache.init().catch(err => {
      CacheLogger.initFailed('L2 cache', err instanceof Error ? err.message : String(err));
    });
    
    // Schedule periodic cleanup (every 5 minutes)
    this.cleanupInterval = setInterval(() => {
      this.performCleanup();
    }, 5 * 60 * 1000);
  }
  
  /**
   * Set the current document context
   * Called when document changes to update cache keys
   */
  setDocumentContext(documentId: string, contentHash: string): void {
    const hashChanged = this.currentContentHash !== contentHash;
    const docChanged = this.currentDocumentId !== documentId;
    
    if (docChanged || hashChanged) {
      // Clear L1 when document or content changes
      // L2 entries are keyed by hash, so old entries naturally become unused
      if (hashChanged && this.l1Cache.size > 0) {
        CacheLogger.orchestrator.contentHashChanged(this.l1Cache.size);
        this.l1Cache.clear();
      }
    }
    
    this.currentDocumentId = documentId;
    this.currentContentHash = contentHash;
  }
  
  /**
   * Get a single page from cache
   * Checks L1 first, then L2, with automatic promotion
   */
  async getPage(pageIndex: number): Promise<CacheLookupResult> {
    const start = performance.now();
    const cacheKey = generatePageCacheKey(this.currentContentHash, pageIndex);
    
    // Check L1 first
    const l1Entry = this.l1Cache.get(cacheKey);
    if (l1Entry) {
      touchLRUEntry(l1Entry);
      this.sessionStats.l1Hits++;
      return {
        page: l1Entry.value,
        source: 'l1',
        latencyMs: performance.now() - start
      };
    }
    
    // Check L2
    const l2Page = await this.l2Cache.get(
      this.currentDocumentId,
      this.currentContentHash,
      pageIndex
    );
    
    if (l2Page) {
      // Promote to L1
      this.promoteToL1(cacheKey, l2Page);
      this.sessionStats.l2Hits++;
      return {
        page: l2Page,
        source: 'l2',
        latencyMs: performance.now() - start
      };
    }
    
    // Cache miss
    this.sessionStats.misses++;
    return {
      page: null,
      source: 'miss',
      latencyMs: performance.now() - start
    };
  }
  
  /**
   * Get multiple pages from cache (batch operation)
   */
  async getPages(pageIndices: number[]): Promise<BatchLookupResult> {
    const start = performance.now();
    const pages = new Map<number, PageLayout>();
    const l2Needed: number[] = [];
    let l1Hits = 0;
    let l2Hits = 0;
    
    // Check L1 for all pages
    for (const pageIndex of pageIndices) {
      const cacheKey = generatePageCacheKey(this.currentContentHash, pageIndex);
      const l1Entry = this.l1Cache.get(cacheKey);
      
      if (l1Entry) {
        touchLRUEntry(l1Entry);
        pages.set(pageIndex, l1Entry.value);
        l1Hits++;
      } else {
        l2Needed.push(pageIndex);
      }
    }
    
    // Batch fetch from L2
    if (l2Needed.length > 0) {
      const l2Pages = await this.l2Cache.getPages(
        this.currentDocumentId,
        this.currentContentHash,
        l2Needed
      );
      
      for (const [pageIndex, page] of l2Pages) {
        pages.set(pageIndex, page);
        // Promote to L1
        const cacheKey = generatePageCacheKey(this.currentContentHash, pageIndex);
        this.promoteToL1(cacheKey, page);
        l2Hits++;
      }
    }
    
    // Calculate misses
    const misses = pageIndices.filter(idx => !pages.has(idx));
    
    // Update session stats
    this.sessionStats.l1Hits += l1Hits;
    this.sessionStats.l2Hits += l2Hits;
    this.sessionStats.misses += misses.length;
    
    return {
      pages,
      l1Hits,
      l2Hits,
      misses,
      latencyMs: performance.now() - start
    };
  }
  
  /**
   * Store a page in cache (both L1 and L2)
   */
  async setPage(page: PageLayout): Promise<void> {
    const cacheKey = generatePageCacheKey(this.currentContentHash, page.page_index);
    
    // Store in L1
    this.l1Cache.set(cacheKey, createLRUEntry(page));
    this.enforceL1Limit();
    
    // Store in L2 (async, don't wait)
    this.l2Cache.set(
      this.currentDocumentId,
      this.currentContentHash,
      page.page_index,
      page
    ).catch(err => {
      CacheLogger.writeFailed('L2 cache', err instanceof Error ? err.message : String(err));
    });
  }
  
  /**
   * Store multiple pages (batch operation)
   */
  async setPages(pages: PageLayout[]): Promise<void> {
    // Store in L1
    for (const page of pages) {
      const cacheKey = generatePageCacheKey(this.currentContentHash, page.page_index);
      this.l1Cache.set(cacheKey, createLRUEntry(page));
    }
    this.enforceL1Limit();
    
    // Batch store in L2 (async, don't wait)
    this.l2Cache.setPages(
      this.currentDocumentId,
      this.currentContentHash,
      pages
    ).catch(err => {
      CacheLogger.writeFailed('L2 cache (batch)', err instanceof Error ? err.message : String(err));
    });
  }
  
  /**
   * Check if document/hash exists in L2 cache
   * Useful for determining if we can load from cache on document open
   */
  async hasPersistedDocument(): Promise<boolean> {
    return this.l2Cache.hasDocument(
      this.currentDocumentId,
      this.currentContentHash
    );
  }
  
  /**
   * Get count of persisted pages for current document
   */
  async getPersistedPageCount(): Promise<number> {
    return this.l2Cache.getPageCount(
      this.currentDocumentId,
      this.currentContentHash
    );
  }
  
  /**
   * Invalidate all cache entries (called when content hash changes)
   */
  async invalidateAll(): Promise<void> {
    // Clear L1
    this.l1Cache.clear();
    
    // L2 entries are keyed by hash, so they naturally become stale
    // We don't need to actively delete them - cleanup will handle it
  }
  
  /**
   * Invalidate pages from a certain index onward
   * (For future partial invalidation support)
   */
  async invalidateFromPage(fromPageIndex: number): Promise<void> {
    // Clear affected L1 entries
    for (const [key, entry] of this.l1Cache) {
      // Parse page index from key (format: hash-pageIndex)
      const parts = key.split('-');
      const pageIndex = parseInt(parts[parts.length - 1], 10);
      if (!isNaN(pageIndex) && pageIndex >= fromPageIndex) {
        this.l1Cache.delete(key);
      }
    }
    
    // Invalidate in L2
    await this.l2Cache.invalidateFromPage(
      this.currentDocumentId,
      this.currentContentHash,
      fromPageIndex
    );
  }
  
  /**
   * Get cache statistics
   */
  async getStats(): Promise<CacheOrchestratorStats> {
    const l2Stats = await this.l2Cache.getStats();
    const totalRequests = this.sessionStats.l1Hits + this.sessionStats.l2Hits + this.sessionStats.misses;
    
    return {
      l1: {
        size: this.l1Cache.size,
        maxSize: L1_MAX_SIZE,
        hitRate: totalRequests > 0 
          ? this.sessionStats.l1Hits / totalRequests 
          : 0
      },
      l2: {
        totalEntries: l2Stats.totalEntries,
        uniqueDocuments: l2Stats.uniqueDocuments
      },
      session: { ...this.sessionStats }
    };
  }
  
  /**
   * Promote a page from L2 to L1
   */
  private promoteToL1(cacheKey: string, page: PageLayout): void {
    this.l1Cache.set(cacheKey, createLRUEntry(page));
    this.enforceL1Limit();
  }
  
  /**
   * Enforce L1 size limit using LRU eviction
   */
  private enforceL1Limit(): void {
    if (this.l1Cache.size > L1_MAX_SIZE) {
      const evicted = evictLRUEntries(this.l1Cache, L1_MAX_SIZE - L1_EVICTION_BATCH);
      if (evicted > 0) {
        CacheLogger.orchestrator.evictLRU(evicted);
      }
    }
  }
  
  /**
   * Perform periodic cleanup
   */
  private async performCleanup(): Promise<void> {
    try {
      await this.l2Cache.cleanup();
    } catch (err) {
      CacheLogger.cleanupFailed('L2 cache', err instanceof Error ? err.message : String(err));
    }
  }
  
  /**
   * Clear all caches
   */
  async clear(): Promise<void> {
    this.l1Cache.clear();
    await this.l2Cache.clear();
    this.sessionStats = { l1Hits: 0, l2Hits: 0, misses: 0 };
  }
  
  /**
   * Dispose of resources
   */
  dispose(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.l1Cache.clear();
    this.l2Cache.close();
  }
}

// Singleton instance
let _orchestrator: CacheOrchestrator | null = null;

export function getCacheOrchestrator(): CacheOrchestrator {
  if (!_orchestrator) {
    _orchestrator = new CacheOrchestrator();
  }
  return _orchestrator;
}
